import { useEffect, useRef, useState } from 'react';

import { cohortsApi } from '../api';
import type { CohortThread, PaginatedResponse } from '../types';

export interface UseCohortThreadsResult {
  threads: CohortThread[];
  loaded: boolean;
  errorStatus: number | null;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
  deleteThread: (
    threadId: string,
    onSuccess: () => void,
    onError: (status: number | null) => void,
  ) => void;
}

/** Identifies one course-selector selection, to detect a stale async response. */
const selectionKeyOf = (slug: string | undefined, courseId: string | null): string => (
  `${slug ?? ''}::${courseId ?? ''}`
);

/**
 * Paginated fetch of a cohort's forum threads for one course. Re-fetches from
 * page 1 whenever `slug`/`courseId` change, and drops any response that lands
 * after the user has since switched selection.
 */
export function useCohortThreads(
  slug: string | undefined,
  courseId: string | null,
): UseCohortThreadsResult {
  const [threads, setThreads] = useState<CohortThread[]>([]);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [nextPage, setNextPage] = useState<number | null>(1);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);

  const activeSelectionRef = useRef<string>(selectionKeyOf(slug, courseId));
  const nextPageRef = useRef<number | null>(1);
  const loadMoreInFlightRef = useRef<boolean>(false);

  const fetchPage = (selectionKey: string, page: number, append: boolean): void => {
    if (!slug || !courseId) {
      return;
    }
    if (append) {
      setIsLoadingMore(true);
    }

    cohortsApi.getCohortThreads(
      slug,
      courseId,
      page,
      (res: PaginatedResponse<CohortThread>) => {
        if (activeSelectionRef.current !== selectionKey) {
          return; // stale response for a selection the user has since left
        }
        nextPageRef.current = res.next ? page + 1 : null;
        setThreads((current) => (append ? [...current, ...res.results] : res.results));
        setNextPage(nextPageRef.current);
        setLoaded(true);
        setIsLoadingMore(false);
        loadMoreInFlightRef.current = false;
      },
      (error) => {
        if (activeSelectionRef.current !== selectionKey) {
          return;
        }
        setErrorStatus((error as { status?: number })?.status ?? null);
        setLoaded(true);
        setIsLoadingMore(false);
        loadMoreInFlightRef.current = false;
      },
    );
  };

  useEffect(() => {
    const selectionKey = selectionKeyOf(slug, courseId);
    activeSelectionRef.current = selectionKey;
    loadMoreInFlightRef.current = false;
    nextPageRef.current = 1;

    setThreads([]);
    setErrorStatus(null);
    setNextPage(1);

    if (slug && courseId) {
      setLoaded(false);
      fetchPage(selectionKey, 1, false);
    } else {
      setLoaded(true);
    }
  }, [slug, courseId]);

  const loadMore = (): void => {
    // A ref, not `isLoadingMore`: the `disabled` attribute it drives only lands
    // on the next render, so a double-click in one tick would fetch page twice.
    if (nextPage && !loadMoreInFlightRef.current) {
      loadMoreInFlightRef.current = true;
      fetchPage(selectionKeyOf(slug, courseId), nextPage, true);
    }
  };

  const deleteThread = (
    threadId: string,
    onSuccess: () => void,
    onError: (status: number | null) => void,
  ): void => {
    if (!slug) {
      return;
    }
    const selectionKey = selectionKeyOf(slug, courseId);

    cohortsApi.deleteCohortThread(
      slug,
      threadId,
      () => {
        if (activeSelectionRef.current === selectionKey) {
          setThreads((current) => {
            const next = current.filter((thread) => thread.id !== threadId);
            if (next.length === 0 && nextPageRef.current) {
              // Otherwise the pager dead-ends behind an empty list.
              fetchPage(selectionKey, nextPageRef.current, true);
            }
            return next;
          });
        }
        onSuccess();
      },
      (error) => {
        onError((error as { status?: number })?.status ?? null);
      },
    );
  };

  return {
    threads, loaded, errorStatus, hasMore: nextPage !== null, isLoadingMore, loadMore, deleteThread,
  };
}
